import { useState, useEffect } from 'react'
import { supabase } from '../supabaseClient'

import { DEFAULT_LOGO } from '../clubLogos'

const POSITIONS = [
  { num: 1,  x: 50, y: 92 }, { num: 2,  x: 85, y: 80 }, { num: 3,  x: 50, y: 80 },
  { num: 4,  x: 15, y: 80 }, { num: 5,  x: 85, y: 65 }, { num: 6,  x: 50, y: 65 },
  { num: 7,  x: 15, y: 65 }, { num: 8,  x: 30, y: 50 }, { num: 9,  x: 70, y: 50 },
  { num: 10, x: 85, y: 35 }, { num: 11, x: 50, y: 35 }, { num: 12, x: 15, y: 35 },
  { num: 13, x: 85, y: 18 }, { num: 14, x: 50, y: 10 }, { num: 15, x: 15, y: 18 },
]

function formatDate(dateStr) {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  return d.toLocaleString('en-IE', { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'UTC' })
}

export default function MatchReportModal({ fixture, onClose, getClubLogo, myClubLogo, currentUserEmail, currentUserName, clubName }) {
  const [lineup, setLineup] = useState([])
  const [subs, setSubs] = useState([])
  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(true)
  const [emailState, setEmailState] = useState(null)
  const [emailTarget, setEmailTarget] = useState(null)

  useEffect(() => { loadData() }, [])

  async function loadData() {
    const [lineupRes, eventsRes] = await Promise.all([
      supabase.from('lineups').select('*, players(full_name, jersey_number)')
        .eq('fixture_id', fixture.id).order('match_number', { ascending: true }),
      supabase.from('match_events').select('*, players(full_name)')
        .eq('fixture_id', fixture.id).order('minute', { ascending: true })
    ])
    const lineupData = lineupRes.data || []
    setLineup(lineupData.filter(l => l.starting))
    setSubs(lineupData.filter(l => !l.starting))
    setEvents(eventsRes.data || [])
    setLoading(false)
  }

  async function sendEmail(target) {
    setEmailTarget(target)
    setEmailState('sending')
    try {
      let recipients = []
      if (target === 'self') {
        recipients = [currentUserEmail]
      } else {
        const { data: members } = await supabase
          .from('squad_members')
          .select('profiles(full_name, email)')
          .eq('squad_id', fixture.squad_id)
          .eq('approved', true)
        recipients = (members || []).map(m => m.profiles?.email).filter(Boolean)
      }
      if (recipients.length === 0) { setEmailState('error'); return }

      const totalHome = fixture.home_goals * 3 + fixture.home_points
      const totalAway = fixture.away_goals * 3 + fixture.away_points
      const result = totalHome > totalAway ? 'Win' : totalHome < totalAway ? 'Loss' : 'Draw'

      const scorerTally = {}
      events.filter(e => ['goal','point','twopoint'].includes(e.event_type)).forEach(e => {
        const key = e.player_id || 'unknown'
        const name = e.players?.full_name || 'Unknown'
        if (!scorerTally[key]) scorerTally[key] = { name, goals: 0, points: 0 }
        if (e.event_type === 'goal') scorerTally[key].goals++
        if (e.event_type === 'point') scorerTally[key].points++
        if (e.event_type === 'twopoint') scorerTally[key].points += 2
      })
      const scorers = Object.values(scorerTally).sort((a,b) => (b.goals*3+b.points)-(a.goals*3+a.points))
      const scorersText = scorers.map(s => `${s.name}: ${s.goals}-${String(s.points).padStart(2,'0')} (${s.goals*3+s.points} pts)`).join('<br>')

      const koWon = events.filter(e => e.event_type === 'kickout_won').length
      const koLost = events.filter(e => e.event_type === 'kickout_lost').length
      const ourWides = events.filter(e => e.event_type === 'wide').length
      const freesWon = events.filter(e => e.event_type === 'free_won').length

      const htmlBody = `
        <div style="font-family: sans-serif; max-width: 520px; margin: 0 auto;">
          <div style="background: linear-gradient(135deg, #0d3d6b 0%, #185FA5 100%); padding: 24px; border-radius: 12px 12px 0 0; text-align: center; color: white;">
            <p style="font-size: 11px; opacity: 0.7; letter-spacing: 1px; margin: 0 0 4px">MATCH REPORT</p>
            <h2 style="margin: 0 0 4px; font-size: 20px;">${fixture.squads?.name || 'Squad'} vs ${fixture.opponent}</h2>
            <p style="margin: 0; opacity: 0.8; font-size: 13px;">${fixture.sport === 'hurling' ? 'Hurling' : 'Football'} · Team ${fixture.team_number || 1} · ${fixture.game_type === 'league' ? 'League' : fixture.game_type === 'championship' ? 'Championship' : 'Friendly'}</p>
            ${fixture.fixture_date ? `<p style="margin: 6px 0 0; opacity: 0.7; font-size: 12px;">${formatDate(fixture.fixture_date)}</p>` : ''}
            ${fixture.venue ? `<p style="margin: 2px 0 0; opacity: 0.7; font-size: 12px;">📍 ${fixture.venue}</p>` : ''}
          </div>
          <div style="background: white; border: 1px solid #e0e0e0; border-top: none; border-radius: 0 0 12px 12px; padding: 20px;">
            <div style="text-align: center; background: #f8f8f8; border-radius: 10px; padding: 16px; margin-bottom: 16px;">
              <p style="font-size: 28px; font-weight: 800; color: #185FA5; margin: 0;">${fixture.home_goals}-${String(fixture.home_points).padStart(2,'0')} (${totalHome}) v ${fixture.away_goals}-${String(fixture.away_points).padStart(2,'00')} (${totalAway})</p>
              <p style="font-size: 14px; font-weight: 600; color: ${totalHome > totalAway ? '#3B6D11' : totalHome < totalAway ? '#A32D2D' : '#854F0B'}; margin: 4px 0 0;">${result}</p>
            </div>
            ${scorers.length > 0 ? `<h3 style="font-size: 12px; color: #888; letter-spacing: 0.5px; margin: 0 0 8px;">SCORERS</h3><p style="font-size: 13px; color: #333; line-height: 1.8;">${scorersText}</p>` : ''}
            <h3 style="font-size: 12px; color: #888; letter-spacing: 0.5px; margin: 16px 0 8px;">MATCH STATS</h3>
            <table style="width: 100%; font-size: 13px; border-collapse: collapse;">
              <tr><td style="padding: 4px 0; color: #555;">Wides</td><td style="text-align: right; font-weight: 600; color: #185FA5;">${ourWides}</td></tr>
              <tr><td style="padding: 4px 0; color: #555;">Kick Outs Won</td><td style="text-align: right; font-weight: 600; color: #185FA5;">${koWon} / ${koWon+koLost}</td></tr>
              <tr><td style="padding: 4px 0; color: #555;">Frees Won</td><td style="text-align: right; font-weight: 600; color: #185FA5;">${freesWon}</td></tr>
            </table>
            ${fixture.tracked_by_name ? `<p style="font-size: 11px; color: #aaa; margin-top: 20px; text-align: center;">Tracked by ${fixture.tracked_by_name}</p>` : ''}
            <p style="font-size: 11px; color: #bbb; text-align: center; margin-top: 4px;">GAA Match Tracker · gaamatchtracker.ie</p>
          </div>
        </div>`

      const res = await fetch('https://mrjtwgvscgcvuecxjcbg.supabase.co/functions/v1/send-invite-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + process.env.REACT_APP_SUPABASE_ANON_KEY },
        body: JSON.stringify({ type: 'match_report', toEmails: recipients, subject: `Match Report: ${fixture.squads?.name || 'Squad'} vs ${fixture.opponent} — ${result}`, htmlBody })
      })
      if (!res.ok) throw new Error('Failed')
      setEmailState('sent')
    } catch (e) {
      setEmailState('error')
    }
  }

  const totalHome = fixture.home_goals * 3 + fixture.home_points
  const totalAway = fixture.away_goals * 3 + fixture.away_points
  const result = totalHome > totalAway ? 'W' : totalHome < totalAway ? 'L' : 'D'
  const resultColor = result === 'W' ? '#3B6D11' : result === 'L' ? '#A32D2D' : '#854F0B'
  const resultBg = result === 'W' ? '#EAF3DE' : result === 'L' ? '#FCEBEB' : '#FAEEDA'
  const resultText = result === 'W' ? 'Win' : result === 'L' ? 'Loss' : 'Draw'

  // Scorers with from_play breakdown
  const scorerTally = {}
  events.filter(e => ['goal','point','twopoint'].includes(e.event_type)).forEach(e => {
    const key = e.player_id || 'unknown'
    const name = e.players?.full_name || 'Unknown'
    if (!scorerTally[key]) scorerTally[key] = { name, goals: 0, points: 0, goalsPlay: 0, pointsPlay: 0 }
    if (e.event_type === 'goal') { scorerTally[key].goals++; if (e.notes === 'from_play') scorerTally[key].goalsPlay++ }
    if (e.event_type === 'point') { scorerTally[key].points++; if (e.notes === 'from_play') scorerTally[key].pointsPlay++ }
    if (e.event_type === 'twopoint') { scorerTally[key].points += 2; if (e.notes === 'from_play') scorerTally[key].pointsPlay += 2 }
  })
  const scorers = Object.values(scorerTally).sort((a,b) => (b.goals*3+b.points)-(a.goals*3+a.points))

  // Half breakdown
  const h1Goals = events.filter(e => e.event_type === 'goal' && typeof e.minute === 'number' && e.minute <= 30).length
  const h1Points = events.filter(e => ['point','twopoint'].includes(e.event_type) && typeof e.minute === 'number' && e.minute <= 30).length
  const h2Goals = events.filter(e => e.event_type === 'goal' && typeof e.minute === 'number' && e.minute > 30).length
  const h2Points = events.filter(e => ['point','twopoint'].includes(e.event_type) && typeof e.minute === 'number' && e.minute > 30).length

  // Stats
  const ourWides = events.filter(e => e.event_type === 'wide').length
  const oppWides = events.filter(e => e.event_type === 'opp_wide').length
  const ourFortyFives = events.filter(e => e.event_type === 'fortyfive_won').length
  const oppFortyFives = events.filter(e => e.event_type === 'fortyfive_conceded').length
  const koWon = events.filter(e => e.event_type === 'kickout_won').length
  const koLost = events.filter(e => e.event_type === 'kickout_lost').length
  const koTotal = koWon + koLost
  const koPercent = koTotal > 0 ? Math.round((koWon / koTotal) * 100) : null
  const freesWon = events.filter(e => e.event_type === 'free_won').length
  const freesConceded = events.filter(e => e.event_type === 'free_conceded').length
  const yellowCards = events.filter(e => e.event_type === 'yellow_card')
  const blackCards = events.filter(e => e.event_type === 'black_card')
  const redCards = events.filter(e => e.event_type === 'red_card')
  const keyEvents = events.filter(e => ['goal','point','twopoint','sub_on','yellow_card','black_card','red_card'].includes(e.event_type))
  const subEvents = events.filter(e => e.event_type === 'sub_on')

  const oppLogo = getClubLogo ? getClubLogo(fixture.opponent) : DEFAULT_LOGO
  const sport = fixture.sport === 'hurling' ? 'Hurling' : 'Football'
  const gameType = fixture.game_type === 'league' ? 'League' : fixture.game_type === 'championship' ? 'Championship' : 'Friendly'

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1000, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', overflowY: 'auto', padding: '16px' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}>
      <div style={{ background: '#f5f5f5', borderRadius: '16px', width: '100%', maxWidth: '480px', overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,0.3)' }}>

        {/* Header */}
        <div style={{ background: 'linear-gradient(135deg, #0d3d6b 0%, #185FA5 100%)', color: 'white', padding: '20px 16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
            <img src={myClubLogo || DEFAULT_LOGO} alt="" style={{ width: '52px', height: '52px', borderRadius: '50%', objectFit: 'contain', background: 'rgba(255,255,255,0.1)', padding: '2px' }} onError={e => { e.target.style.display='none' }} />
            <div style={{ textAlign: 'center', flex: 1, padding: '0 12px' }}>
              <p style={{ fontSize: '11px', opacity: 0.7, letterSpacing: '1px', marginBottom: '2px' }}>MATCH REPORT</p>
              <p style={{ fontSize: '16px', fontWeight: '700' }}>{fixture.squads?.name || 'Squad'}</p>
              <p style={{ fontSize: '11px', opacity: 0.8 }}>{sport} · Team {fixture.team_number || 1} · {gameType}</p>
            </div>
            <img src={oppLogo} alt="" style={{ width: '52px', height: '52px', borderRadius: '50%', objectFit: 'contain', background: 'rgba(255,255,255,0.1)', padding: '2px' }} onError={e => { e.target.src = DEFAULT_LOGO }} />
          </div>

          {/* Score */}
          <div style={{ background: 'rgba(255,255,255,0.12)', borderRadius: '12px', padding: '14px', textAlign: 'center', marginBottom: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ flex: 1 }}>
                <p style={{ fontSize: '11px', opacity: 0.7, marginBottom: '4px' }}>{clubName || 'Our Team'}</p>
                <p style={{ fontSize: '36px', fontWeight: '800', letterSpacing: '-1px', lineHeight: 1 }}>{fixture.home_goals}-{String(fixture.home_points).padStart(2,'0')}</p>
                <p style={{ fontSize: '12px', opacity: 0.7 }}>({totalHome} pts)</p>
              </div>
              <div style={{ textAlign: 'center', padding: '0 8px' }}>
                <span style={{ fontSize: '13px', fontWeight: '700', padding: '4px 12px', borderRadius: '20px', background: resultBg, color: resultColor }}>{resultText}</span>
              </div>
              <div style={{ flex: 1, textAlign: 'right' }}>
                <p style={{ fontSize: '11px', opacity: 0.7, marginBottom: '4px' }}>{fixture.opponent}</p>
                <p style={{ fontSize: '36px', fontWeight: '800', letterSpacing: '-1px', lineHeight: 1 }}>{fixture.away_goals}-{String(fixture.away_points).padStart(2,'00')}</p>
                <p style={{ fontSize: '12px', opacity: 0.7 }}>({totalAway} pts)</p>
              </div>
            </div>
            {fixture.ht_home_goals != null && (
              <p style={{ fontSize: '12px', opacity: 0.75, marginTop: '10px', textAlign: 'center' }}>
                HT: {fixture.ht_home_goals}-{String(fixture.ht_home_points).padStart(2,'0')} to {fixture.ht_away_goals}-{String(fixture.ht_away_points).padStart(2,'0')}
              </p>
            )}
          </div>

          {/* Match info */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', fontSize: '11px' }}>
            {fixture.fixture_date && (
              <div style={{ background: 'rgba(255,255,255,0.1)', borderRadius: '8px', padding: '8px' }}>
                <p style={{ opacity: 0.6, marginBottom: '2px' }}>DATE</p>
                <p style={{ fontWeight: '500' }}>{formatDate(fixture.fixture_date)}</p>
              </div>
            )}
            {fixture.venue && (
              <div style={{ background: 'rgba(255,255,255,0.1)', borderRadius: '8px', padding: '8px' }}>
                <p style={{ opacity: 0.6, marginBottom: '2px' }}>VENUE</p>
                <p style={{ fontWeight: '500' }}>{fixture.venue}</p>
              </div>
            )}
            {fixture.tracked_by_name && (
              <div style={{ background: 'rgba(255,255,255,0.1)', borderRadius: '8px', padding: '8px' }}>
                <p style={{ opacity: 0.6, marginBottom: '2px' }}>TRACKED BY</p>
                <p style={{ fontWeight: '500' }}>{fixture.tracked_by_name}</p>
              </div>
            )}
          </div>
        </div>

        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#888' }}>Loading report...</div>
        ) : (<>

          {/* Pitch lineup — uses match_number to place players 1-15 */}
          {lineup.length > 0 && (
            <div style={{ background: 'white', margin: '12px 12px 0', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
              <p style={{ fontSize: '11px', fontWeight: '600', color: '#888', letterSpacing: '0.5px', padding: '10px 14px 8px' }}>STARTING LINEUP</p>
              <div style={{ position: 'relative', width: '100%', paddingBottom: '170%', background: '#2d7a3a' }}>
                <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} viewBox="0 0 100 170">
                  <rect width="100" height="170" fill="#2d7a3a" />
                  {[0,1,2,3,4,5,6,7,8,9].map(i => <rect key={i} x="0" y={i*17} width="100" height="17" fill={i%2===0?'#2d7a3a':'#317d3e'} />)}
                  <rect x="3" y="3" width="94" height="164" fill="none" stroke="white" strokeWidth="0.8" />
                  <line x1="3" y1="85" x2="97" y2="85" stroke="white" strokeWidth="0.8" />
                  <rect x="13" y="3" width="74" height="24" fill="none" stroke="white" strokeWidth="0.7" />
                  <rect x="28" y="3" width="44" height="12" fill="none" stroke="white" strokeWidth="0.7" />
                  <rect x="13" y="143" width="74" height="24" fill="none" stroke="white" strokeWidth="0.7" />
                  <rect x="28" y="155" width="44" height="12" fill="none" stroke="white" strokeWidth="0.7" />
                </svg>
                {POSITIONS.map(pos => {
                  // Match by match_number first, fall back to position_override
                const player = lineup.find(l => Number(l.position_override) === pos.num)
                  if (!player) return (
                    <div key={pos.num} style={{ position: 'absolute', left: pos.x+'%', top: pos.y+'%', transform: 'translate(-50%,-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px', width: '64px', zIndex: 2 }}>
                      <div style={{ width: '34px', height: '34px', borderRadius: '50%', background: 'rgba(255,255,255,0.1)', border: '2px dashed rgba(255,255,255,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: '700', color: 'rgba(255,255,255,0.5)' }}>{pos.num}</div>
                    </div>
                  )
                  const name = player.players?.full_name || ''
                  const [firstName, ...rest] = name.split(' ')
                  const lastName = rest.join(' ')
                  const displayNum = player.match_number || player.position_override || pos.num
                  return (
                    <div key={pos.num} style={{ position: 'absolute', left: pos.x+'%', top: pos.y+'%', transform: 'translate(-50%,-50%)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px', width: '60px', zIndex: 2 }}>
                      <div style={{ width: '34px', height: '34px', borderRadius: '50%', background: '#185FA5', border: '2px solid white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: '700', color: 'white', boxShadow: '0 2px 4px rgba(0,0,0,0.4)' }}>{displayNum}</div>
                      <span style={{ fontSize: '8px', color: 'white', fontWeight: '500', textShadow: '0 1px 3px rgba(0,0,0,0.9)', textAlign: 'center', width: '60px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{firstName}</span>
                      <span style={{ fontSize: '8px', color: 'white', fontWeight: '700', textShadow: '0 1px 3px rgba(0,0,0,0.9)', textAlign: 'center', width: '60px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{lastName}</span>
                    </div>
                  )
                })}
              </div>
              {subs.length > 0 && (
                <div style={{ padding: '10px 14px 12px' }}>
                  <p style={{ fontSize: '10px', color: '#888', fontWeight: '600', marginBottom: '6px' }}>SUBSTITUTES</p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                    {subs.map((s, i) => (
                      <span key={i} style={{ fontSize: '11px', padding: '3px 8px', borderRadius: '20px', background: '#f0f0f0', color: '#333' }}>
                        {s.match_number || s.position_override} · {s.players?.full_name?.split(' ').slice(-1)[0]}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Half breakdown */}
          <div style={{ background: 'white', margin: '12px 12px 0', borderRadius: '12px', padding: '12px 14px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
            <p style={{ fontSize: '11px', fontWeight: '600', color: '#888', letterSpacing: '0.5px', marginBottom: '10px' }}>HALF-TIME BREAKDOWN</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              {[{ label: '1st Half', goals: h1Goals, points: h1Points }, { label: '2nd Half', goals: h2Goals, points: h2Points }].map((h, i) => (
                <div key={i} style={{ background: '#f8f8f8', borderRadius: '8px', padding: '10px', textAlign: 'center' }}>
                  <p style={{ fontSize: '10px', color: '#888', marginBottom: '4px' }}>{h.label}</p>
                  <p style={{ fontSize: '20px', fontWeight: '700', color: '#185FA5' }}>{h.goals}-{String(h.points).padStart(2,'0')}</p>
                  <p style={{ fontSize: '11px', color: '#888' }}>({h.goals*3+h.points} pts)</p>
                </div>
              ))}
            </div>
          </div>

          {/* Scorers */}
          {scorers.length > 0 && (
            <div style={{ background: 'white', margin: '12px 12px 0', borderRadius: '12px', padding: '12px 14px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
              <p style={{ fontSize: '11px', fontWeight: '600', color: '#888', letterSpacing: '0.5px', marginBottom: '10px' }}>SCORERS</p>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '8px', padding: '0 0 6px', borderBottom: '0.5px solid #f0f0f0', marginBottom: '4px' }}>
                <span style={{ fontSize: '10px', color: '#aaa' }}></span>
                <span style={{ fontSize: '10px', color: '#aaa', textAlign: 'right', minWidth: '60px' }}>Total</span>
                <span style={{ fontSize: '10px', color: '#aaa', textAlign: 'right', minWidth: '60px' }}>From Play</span>
              </div>
              {scorers.map((s, i) => (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '8px', alignItems: 'center', padding: '6px 0', borderBottom: i < scorers.length-1 ? '0.5px solid #f5f5f5' : 'none' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontSize: '12px', fontWeight: '700', color: i === 0 ? '#854F0B' : '#bbb', minWidth: '16px' }}>{i+1}</span>
                    <span style={{ fontSize: '13px' }}>{s.name}</span>
                  </div>
                  <span style={{ fontSize: '13px', fontWeight: '700', color: '#185FA5', textAlign: 'right', minWidth: '60px' }}>
                    {s.goals}-{String(s.points).padStart(2,'0')}
                    <span style={{ fontSize: '10px', color: '#888', fontWeight: '400' }}> ({s.goals*3+s.points})</span>
                  </span>
                  <span style={{ fontSize: '12px', color: (s.goalsPlay > 0 || s.pointsPlay > 0) ? '#2d7a3a' : '#ccc', textAlign: 'right', minWidth: '60px' }}>
                    {(s.goalsPlay > 0 || s.pointsPlay > 0) ? `${s.goalsPlay}-${String(s.pointsPlay).padStart(2,'0')}` : '—'}
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Stats */}
          <div style={{ background: 'white', margin: '12px 12px 0', borderRadius: '12px', padding: '12px 14px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
            <p style={{ fontSize: '11px', fontWeight: '600', color: '#888', letterSpacing: '0.5px', marginBottom: '10px' }}>MATCH STATS</p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              {[
                { label: 'Wides', us: ourWides, them: oppWides },
                { label: '45s / 65s', us: ourFortyFives, them: oppFortyFives },
                { label: 'Kick Outs Won', us: koWon, them: koLost, extra: koPercent !== null ? koPercent + '%' : null },
                { label: 'Frees Won', us: freesWon, them: freesConceded },
              ].map((stat, i) => (
                <div key={i} style={{ background: '#f8f8f8', borderRadius: '8px', padding: '10px 12px' }}>
                  <p style={{ fontSize: '10px', color: '#888', marginBottom: '4px' }}>{stat.label}</p>
                  <p style={{ fontSize: '16px', fontWeight: '600', color: '#185FA5' }}>
                    {stat.us} <span style={{ color: '#aaa', fontWeight: '400', fontSize: '13px' }}>— {stat.them}</span>
                  </p>
                  {stat.extra && <p style={{ fontSize: '10px', color: '#888', marginTop: '2px' }}>Won {stat.extra}</p>}
                </div>
              ))}
            </div>
          </div>

          {/* Cards */}
          {(yellowCards.length > 0 || blackCards.length > 0 || redCards.length > 0) && (
            <div style={{ background: 'white', margin: '12px 12px 0', borderRadius: '12px', padding: '12px 14px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
              <p style={{ fontSize: '11px', fontWeight: '600', color: '#888', letterSpacing: '0.5px', marginBottom: '10px' }}>DISCIPLINARY</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                {yellowCards.map((e,i) => <span key={i} style={{ fontSize: '12px', padding: '4px 10px', borderRadius: '20px', background: '#FAEEDA', color: '#854F0B' }}>🟨 {e.players?.full_name} {typeof e.minute === 'number' ? e.minute+"'" : e.minute||''}</span>)}
                {blackCards.map((e,i) => <span key={i} style={{ fontSize: '12px', padding: '4px 10px', borderRadius: '20px', background: '#f0f0f0', color: '#333' }}>⬛ {e.players?.full_name} {typeof e.minute === 'number' ? e.minute+"'" : e.minute||''}</span>)}
                {redCards.map((e,i) => <span key={i} style={{ fontSize: '12px', padding: '4px 10px', borderRadius: '20px', background: '#FCEBEB', color: '#A32D2D' }}>🟥 {e.players?.full_name} {typeof e.minute === 'number' ? e.minute+"'" : e.minute||''}</span>)}
              </div>
            </div>
          )}

          {/* Key events */}
          {keyEvents.length > 0 && (
            <div style={{ background: 'white', margin: '12px 12px 0', borderRadius: '12px', padding: '12px 14px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
              <p style={{ fontSize: '11px', fontWeight: '600', color: '#888', letterSpacing: '0.5px', marginBottom: '10px' }}>KEY EVENTS</p>
              {keyEvents.map((e, i) => {
                const icon = e.event_type === 'goal' ? '⚽' : e.event_type === 'point' ? '🎯' : e.event_type === 'twopoint' ? '🎯🎯' : e.event_type === 'sub_on' ? '🔄' : e.event_type === 'yellow_card' ? '🟨' : e.event_type === 'black_card' ? '⬛' : '🟥'
                const label = e.event_type === 'goal' ? 'Goal' : e.event_type === 'point' ? 'Point' : e.event_type === 'twopoint' ? '2-pointer' : e.event_type === 'sub_on' ? 'Sub' : e.event_type === 'yellow_card' ? 'Yellow' : e.event_type === 'black_card' ? 'Black Card' : 'Red Card'
                const minuteStr = typeof e.minute === 'number' ? e.minute+"'" : e.minute || ''
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '5px 0', borderBottom: i < keyEvents.length-1 ? '0.5px solid #f5f5f5' : 'none' }}>
                    <span style={{ fontSize: '14px', minWidth: '20px' }}>{icon}</span>
                    <span style={{ fontSize: '11px', color: '#aaa', minWidth: '32px' }}>{minuteStr}</span>
                    <span style={{ fontSize: '13px', flex: 1 }}>{e.players?.full_name || label}</span>
                    <span style={{ fontSize: '11px', color: '#888' }}>{label}</span>
                  </div>
                )
              })}
            </div>
          )}

          {/* Substitutions */}
          {subEvents.length > 0 && (
            <div style={{ background: 'white', margin: '12px 12px 0', borderRadius: '12px', padding: '12px 14px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
              <p style={{ fontSize: '11px', fontWeight: '600', color: '#888', letterSpacing: '0.5px', marginBottom: '10px' }}>SUBSTITUTIONS</p>
              {subEvents.map((e, i) => {
                const subOff = events.find(ev => ev.event_type === 'sub_off' && ev.minute === e.minute)
                const minuteStr = typeof e.minute === 'number' ? e.minute+"'" : e.minute || ''
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '5px 0', borderBottom: i < subEvents.length-1 ? '0.5px solid #f5f5f5' : 'none' }}>
                    <span style={{ fontSize: '11px', color: '#aaa', minWidth: '32px' }}>{minuteStr}</span>
                    <span style={{ fontSize: '12px', color: '#3B6D11' }}>▲ {e.players?.full_name}</span>
                    {subOff && <span style={{ fontSize: '12px', color: '#A32D2D' }}>▼ {subOff.players?.full_name}</span>}
                  </div>
                )
              })}
            </div>
          )}

          {/* Email */}
          <div style={{ background: 'white', margin: '12px 12px 0', borderRadius: '12px', padding: '12px 14px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
            <p style={{ fontSize: '11px', fontWeight: '600', color: '#888', letterSpacing: '0.5px', marginBottom: '10px' }}>EMAIL REPORT</p>
            {emailState === 'sent' ? (
              <p style={{ fontSize: '13px', color: '#3B6D11', textAlign: 'center', padding: '8px' }}>✅ Report emailed {emailTarget === 'self' ? 'to you' : 'to all coaches'}</p>
            ) : emailState === 'error' ? (
              <p style={{ fontSize: '13px', color: '#A32D2D', textAlign: 'center', padding: '8px' }}>❌ Failed to send. Please try again.</p>
            ) : emailState === 'sending' ? (
              <p style={{ fontSize: '13px', color: '#888', textAlign: 'center', padding: '8px' }}>Sending...</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <button onClick={() => sendEmail('self')} style={{ padding: '10px', background: '#E6F1FB', color: '#185FA5', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '500', cursor: 'pointer' }}>
                  📧 Email to myself
                </button>
                <button onClick={() => sendEmail('all')} style={{ padding: '10px', background: '#185FA5', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '500', cursor: 'pointer' }}>
                  📨 Email to all coaches
                </button>
              </div>
            )}
          </div>

          <div style={{ padding: '12px', textAlign: 'center' }}>
            <p style={{ fontSize: '11px', color: '#bbb' }}>GAA Match Tracker · gaamatchtracker.ie</p>
          </div>
        </>)}

        {/* Close */}
        <div style={{ padding: '0 12px 16px' }}>
          <button onClick={onClose} style={{ width: '100%', padding: '12px', background: '#185FA5', color: 'white', border: 'none', borderRadius: '10px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' }}>
            Close Report
          </button>
        </div>
      </div>
    </div>
  )
}